import { CREATIVE_GROUP_NAME_MAX_LENGTH } from '@hprofits/api/creative-group';
import { useAdUnitTypes, useCreateCreativeGroup } from '@hprofits/api-client';
import { AdFormat, AdFormatId, AdFormatIdSchema, AdType, AdTypeId, AdTypeIdSchema } from '@hprofits/shared';
import type { CampaignGroupId } from '@hprofits/shared/types';
import { firstOrNone, formatDimensionSize } from '@hprofits/utils';
import { fMap, hasValue, isEmpty, optional } from '@hprofits/utils/fp/optional';
import { ComputedRef, MaybeRefOrGetter, Ref, toValue } from 'vue';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { apiClient } from '@/api-client/client';
import { justCreatedState } from '@/modules/campaigns/composables/useOnJustCreated';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';

export type BannerFormatSelection = {
  typeId: AdTypeId;
  format: { id: AdFormatId; name: AdFormat };
};

export type UseCreativeTypeFlowOptions = {
  campaignGroupId: MaybeRefOrGetter<CampaignGroupId>;
  existingNames: ComputedRef<Array<string>>;
  onCreated?: () => void | Promise<void>;
};

export type UseCreativeTypeFlowReturn = {
  isTypeDropdownOpen: Ref<boolean>;
  isBannerModalOpen: Ref<boolean>;
  selectedType: Ref<AdType | null>;
  isCreating: Ref<boolean>;
  openTypeDropdown: () => void;
  closeTypeDropdown: () => void;
  selectType: (type: AdType) => void | Promise<void>;
  selectBannerFormat: (selection: BannerFormatSelection) => Promise<void>;
  cancelBannerModal: () => void;
  generateUniqueName: (type: AdType, format?: string) => string;
};

/**
 * Returns unique "{baseName}" / "{baseName} (1)" / "{baseName} (2)" etc., clamped to `maxLength`.
 *
 * The clamp exists because this name is machine-built from an `ad_unit_formats` row, which is a
 * `text` column with no length bound — a long enough format name would push the result past
 * CreateCreativeGroupSchema's cap and fail the create. The user cannot edit or even see that name
 * beforehand, and both call sites swallow the rejection into a generic toast, so a truncated name
 * beats an undiagnosable failure. No known format comes close to triggering it.
 *
 * The stem is re-cut per counter rather than against a fixed suffix budget, so widening from
 * " (9)" to " (10)" takes the extra character out of the stem instead of the cap.
 */
const generateWindowsStyleName = (
  baseName: string,
  existingNames: ReadonlyArray<string>,
  maxLength: number,
): string => {
  const fitted = baseName.length <= maxLength ? baseName : baseName.slice(0, maxLength).trimEnd();
  if (!existingNames.includes(fitted)) {
    return fitted;
  }

  let counter = 1;
  for (;;) {
    const suffix = ` (${counter})`;
    const stem = fitted.slice(0, Math.max(1, maxLength - suffix.length)).trimEnd();
    const candidate = `${stem}${suffix}`;
    if (!existingNames.includes(candidate)) {
      return candidate;
    }
    counter++;
  }
};

export const useCreativeTypeFlow = ({
  campaignGroupId,
  existingNames,
  onCreated,
}: UseCreativeTypeFlowOptions): UseCreativeTypeFlowReturn => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();

  const isTypeDropdownOpen = ref(false);
  const isBannerModalOpen = ref(false);
  const selectedType = ref<AdType | null>(null);

  const createCreativeGroupMutation = useCreateCreativeGroup(apiClient);
  // Manual gate: stays true through mutate → onCreated (refetchSummary) → router.push
  // so the next click reads a cache that already reflects the new creative group.
  const isCreating = ref(false);

  const openTypeDropdown = (): void => {
    isTypeDropdownOpen.value = true;
  };

  const closeTypeDropdown = (): void => {
    isTypeDropdownOpen.value = false;
  };

  const selectType = async (type: AdType): Promise<void> => {
    selectedType.value = type;
    closeTypeDropdown();

    if (type === 'Banner') {
      isBannerModalOpen.value = true;
    } else if (
      type === 'Popunder' ||
      type === 'Interstitial' ||
      type === 'Native' ||
      type === 'Link' ||
      type === 'Video' ||
      type === 'VideoSlider' ||
      type === 'OutstreamVideo'
    ) {
      await createSimpleCreativeGroup(type);
    }
  };

  const adTypesQuery = useAdUnitTypes(apiClient, 'full');
  const createSimpleCreativeGroup = async (type: AdType): Promise<void> => {
    const activeCampaignId = toValue(campaignGroupId);

    isCreating.value = true;
    try {
      const response = await adTypesQuery.suspense();
      if (hasValue(response.error)) {
        throw new Error(`Cannot load at types: ${response.error.message}`);
      }
      const adUnitData = fMap(optional(response.data), (d) => d.find((v) => v.name === type));
      if (isEmpty(adUnitData)) {
        throw new Error(`Ad type [${type}] is not supported by the server`);
      }

      const firstFormat = firstOrNone(adUnitData.formats);
      if (!firstFormat) {
        throw new Error(`No formats available for ${type} type`);
      }

      const name = generateUniqueName(type);

      const createdGroup = await createCreativeGroupMutation.mutateAsync({
        campaignId: activeCampaignId,
        body: {
          name,
          type: adUnitData.id,
          format: firstFormat.id,
          status: 'enabled',
        },
      });

      selectedType.value = null;

      // Refetch summary before navigation so sidebar is updated
      await onCreated?.();
      const routeCreativeGroup = toValue(routeResolver.creativeGroup(toValue(campaignGroupId), createdGroup.id));
      await router.push({
        ...routeCreativeGroup,
        state: justCreatedState(String(createdGroup.id)),
      });
    } catch (error) {
      console.error(`Failed to create ${type} creative group:`, error);
      selectedType.value = null;
    } finally {
      isCreating.value = false;
    }
  };

  const cancelBannerModal = (): void => {
    isBannerModalOpen.value = false;
    selectedType.value = null;
  };

  const generateUniqueName = (type: AdType, formatName?: string): string => {
    // Trim: some ad_unit_formats rows carry a legacy trailing space (e.g. 'Desktop Wide
    // Footer (900x250) ', issue #520). The backend `.trim()`s the name on write, so
    // existingNames contains the trimmed form — without trimming here too, the suffix
    // check would miss and we'd POST a duplicate.
    const baseName = (formatName ? `${type} ${formatDimensionSize(formatName)}` : type).trim();
    return generateWindowsStyleName(baseName, existingNames.value, CREATIVE_GROUP_NAME_MAX_LENGTH);
  };

  const selectBannerFormat = async (selection: BannerFormatSelection): Promise<void> => {
    const activeCampaignId = toValue(campaignGroupId);

    const { typeId, format } = selection;
    const name = generateUniqueName('Banner', format.name);

    isCreating.value = true;
    try {
      const createdGroup = await createCreativeGroupMutation.mutateAsync({
        campaignId: activeCampaignId,
        body: {
          name,
          type: AdTypeIdSchema.parse(typeId),
          format: AdFormatIdSchema.parse(format.id),
          status: 'enabled',
        },
      });

      isBannerModalOpen.value = false;
      selectedType.value = null;

      // Refetch summary before navigation so sidebar is updated
      await onCreated?.();

      const routeCreativeGroup = routeResolver.creativeGroup(activeCampaignId, createdGroup.id);
      await router.push({
        ...routeCreativeGroup,
        state: justCreatedState(String(createdGroup.id)),
      });
    } catch (error) {
      console.error('Failed to create creative group:', error);
      // Keep modal open on error so user can retry
    } finally {
      isCreating.value = false;
    }
  };

  return {
    isTypeDropdownOpen,
    isBannerModalOpen,
    selectedType,
    isCreating,
    openTypeDropdown,
    closeTypeDropdown,
    selectType,
    selectBannerFormat,
    cancelBannerModal,
    generateUniqueName,
  };
};
